import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import LabeledElement from '@rio-cloud/rio-uikit/LabeledElement';
import Notification from '@rio-cloud/rio-uikit/Notification';
import Select, { type SelectOption } from '@rio-cloud/rio-uikit/Select';

import { useGetAvailableMessagesQuery, type VehicleConfig } from '@/services/dfpFrontendApi';
import { useTriggerArbitraryMessageMutation } from '@/services/triggerDfpMessageApi';
import {
    buildOverriddenLinesSnapshot,
    getActiveOverriddenLines,
    type OverriddenLinesSnapshot,
} from '../../utils/jsonPathLineResolver';
import JsonEditor from './JsonEditor';

export interface ArbitraryTarget {
    vin: string;
    cid: number;
    version: number;
    jsonContent?: string;
    overriddenJsonPaths?: string[];
}

interface ArbitraryInputModalProps {
    isOpen: boolean;
    onClose: () => void;
    vehicles: VehicleConfig[];
    initialValues?: ArbitraryTarget | null;
}

const EMPTY_JSON_CONTENT = '{\n  \n}';

const ArbitraryInputModal = ({ isOpen, onClose, vehicles, initialValues }: ArbitraryInputModalProps) => {
    const intl = useIntl();
    const [selectedVin, setSelectedVin] = useState('');
    const [selectedCid, setSelectedCid] = useState('');
    const [selectedVersion, setSelectedVersion] = useState('');
    const [jsonContent, setJsonContent] = useState(EMPTY_JSON_CONTENT);
    const [overriddenLinesSnapshot, setOverriddenLinesSnapshot] = useState<OverriddenLinesSnapshot>(new Map());

    const { data: availableMessages } = useGetAvailableMessagesQuery(undefined, { skip: !isOpen });
    const [triggerMessage, { isLoading }] = useTriggerArbitraryMessageMutation();

    // Pre-fill fields when opened with initial values (e.g. from "Use in Arbitrary"). When
    // opened without initial values (the generic "Arbitrary Input" button), there is no known
    // "based on this configured message" context, so any override tags left over from a
    // previous session must be cleared — otherwise they'd keep showing without the user having
    // any way to tell which message they came from.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        if (initialValues) {
            setSelectedVin(initialValues.vin);
            setSelectedCid(initialValues.cid.toString());
            setSelectedVersion(initialValues.version.toString());
            if (initialValues.jsonContent) {
                // Normalize formatting so line numbers line up with resolveOverriddenLines,
                // which assumes JSON.stringify(value, null, 2) formatting.
                let normalizedContent = initialValues.jsonContent;
                try {
                    normalizedContent = JSON.stringify(JSON.parse(initialValues.jsonContent), null, 2);
                } catch {
                    // Leave content as-is if it isn't valid JSON.
                }
                setJsonContent(normalizedContent);
                setOverriddenLinesSnapshot(
                    buildOverriddenLinesSnapshot(normalizedContent, initialValues.overriddenJsonPaths ?? []),
                );
            } else {
                setOverriddenLinesSnapshot(new Map());
            }
        } else {
            setOverriddenLinesSnapshot(new Map());
        }
    }, [isOpen, initialValues]);

    const vehicleOptions: SelectOption[] = vehicles.map((v) => ({
        id: v.vin,
        label: `${v.asset_name} — ${v.vin.slice(-8)}`,
    }));

    const cidOptions: SelectOption[] = (availableMessages ?? []).map((m) => ({
        id: m.cid.toString(),
        label: `CID ${m.cid}`,
    }));

    const selectedMsg = availableMessages?.find((m) => m.cid.toString() === selectedCid);
    const versionOptions: SelectOption[] = (selectedMsg?.versions ?? []).map((v) => ({
        id: v.toString(),
        label: `v${v}`,
    }));

    const handleCidChange = (option: SelectOption | undefined) => {
        setSelectedCid(option?.id ?? '');
        setSelectedVersion('');
    };

    const handleFormatJson = () => {
        try {
            setJsonContent(JSON.stringify(JSON.parse(jsonContent), null, 2));
        } catch {
            // invalid JSON
        }
    };

    const handleJsonContentChange = (value: string) => {
        setJsonContent(value);
        // Tags are re-evaluated per line against overriddenLinesSnapshot (see JsonEditor /
        // getActiveOverriddenLines): a tag only disappears once its own line's text changes,
        // not whenever any part of the payload is edited.
    };

    const handleClear = () => {
        setSelectedVin('');
        setSelectedCid('');
        setSelectedVersion('');
        setJsonContent(EMPTY_JSON_CONTENT);
        setOverriddenLinesSnapshot(new Map());
    };

    const handleDispatch = async () => {
        try {
            const payload = JSON.parse(jsonContent);
            await triggerMessage({
                vin: selectedVin,
                cid: parseInt(selectedCid, 10),
                version: parseInt(selectedVersion, 10),
                jsonMessage: payload,
            }).unwrap();
            Notification.success(intl.formatMessage({ id: 'intl-msg:arbitraryInput.success' }));
            onClose();
        } catch (err: unknown) {
            const status = (err as { status?: number })?.status;
            const msg =
                status === 403
                    ? intl.formatMessage({ id: 'intl-msg:common.error.insufficientPermissions' })
                    : intl.formatMessage({ id: 'intl-msg:common.error.failedToDispatch' });
            Notification.error(msg);
        }
    };

    const isReady = selectedVin && selectedCid && selectedVersion && jsonContent.trim();
    const selectedVehicle = vehicles.find((v) => v.vin === selectedVin);
    const activeOverriddenLinesCount = useMemo(
        () => getActiveOverriddenLines(jsonContent, overriddenLinesSnapshot).size,
        [jsonContent, overriddenLinesSnapshot],
    );
    const hasContentToClear = Boolean(
        selectedVin || selectedCid || selectedVersion || jsonContent !== EMPTY_JSON_CONTENT,
    );

    const body = (
        <div className="display-flex flex-column height-100pct">
            <div className="row margin-bottom-20 flex-0-0-auto">
                <div className="col-md-4">
                    <LabeledElement label={intl.formatMessage({ id: 'intl-msg:arbitraryInput.vehicleLabel' })}>
                        <Select
                            options={vehicleOptions}
                            value={selectedVin ? [selectedVin] : []}
                            onChange={(opt) => setSelectedVin(opt?.id ?? '')}
                            placeholder={intl.formatMessage({ id: 'intl-msg:arbitraryInput.vehiclePlaceholder' })}
                            useFilter
                        />
                    </LabeledElement>
                </div>
                <div className="col-md-4">
                    <LabeledElement label={intl.formatMessage({ id: 'intl-msg:arbitraryInput.cidLabel' })}>
                        <Select
                            options={cidOptions}
                            value={selectedCid ? [selectedCid] : []}
                            onChange={handleCidChange}
                            placeholder={intl.formatMessage({ id: 'intl-msg:arbitraryInput.cidPlaceholder' })}
                            useFilter
                        />
                    </LabeledElement>
                </div>
                <div className="col-md-4">
                    <LabeledElement label={intl.formatMessage({ id: 'intl-msg:arbitraryInput.versionLabel' })}>
                        <Select
                            options={versionOptions}
                            value={selectedVersion ? [selectedVersion] : []}
                            onChange={(opt) => setSelectedVersion(opt?.id ?? '')}
                            placeholder={intl.formatMessage({ id: 'intl-msg:arbitraryInput.versionPlaceholder' })}
                            disabled={!selectedCid}
                        />
                    </LabeledElement>
                </div>
            </div>

            <div className="display-flex flex-column flex-1-1 min-height-0">
                <div className="display-flex justify-content-between align-items-center margin-bottom-10 flex-0-0-auto">
                    <div className="display-flex align-items-center gap-10">
                        <span className="text-bold text-size-12">
                            <FormattedMessage id="intl-msg:arbitraryInput.payloadLabel" />
                        </span>
                        {isReady && (
                            <span className="label label-success label-condensed">
                                <span className="uikit-icon icon-ok margin-right-3" />
                                <FormattedMessage id="intl-msg:arbitraryInput.readyToInject" />
                            </span>
                        )}
                        {activeOverriddenLinesCount > 0 && (
                            <span className="label label-warning label-condensed">
                                <span className="uikit-icon icon-flash margin-right-3" />
                                <FormattedMessage
                                    id="intl-msg:arbitraryInput.overriddenFieldsCount"
                                    values={{ count: activeOverriddenLinesCount }}
                                />
                            </span>
                        )}
                    </div>
                    <Button variant="link" bsSize="sm" onClick={handleFormatJson}>
                        <span className="uikit-icon icon-detail-view margin-right-5" />
                        <FormattedMessage id="intl-msg:arbitraryInput.formatJson" />
                    </Button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', minHeight: 0 }}>
                    <JsonEditor
                        value={jsonContent}
                        onChange={handleJsonContentChange}
                        overriddenLinesSnapshot={overriddenLinesSnapshot}
                    />
                </div>
                <div
                    className={[
                        'display-flex justify-content-between padding-y-5',
                        'text-size-10 text-color-gray flex-0-0-auto',
                    ].join(' ')}
                >
                    <span>UTF-8 • Lines: {jsonContent.split('\n').length}</span>
                </div>
            </div>
        </div>
    );

    const footer = (
        <div className="display-flex justify-content-between align-items-center width-100pct">
            <div>
                <div className="text-size-10 text-bold text-color-gray text-uppercase">
                    <FormattedMessage id="intl-msg:arbitraryInput.commandSummary" />
                </div>
                <div className="text-size-12 text-color-dark">
                    {selectedVin
                        ? intl.formatMessage(
                              { id: 'intl-msg:arbitraryInput.target' },
                              { name: selectedVehicle?.asset_name },
                          )
                        : intl.formatMessage({ id: 'intl-msg:arbitraryInput.noTarget' })}
                    {selectedCid && ` • CID: ${selectedCid}`}
                    {selectedVersion && ` • v${selectedVersion}`}
                </div>
            </div>
            <div className="display-flex gap-10">
                <Button onClick={handleClear} disabled={!hasContentToClear}>
                    <span className="uikit-icon icon-erase margin-right-5" />
                    <FormattedMessage id="intl-msg:arbitraryInput.clear" />
                </Button>
                <Button onClick={onClose}>
                    <FormattedMessage id="intl-msg:arbitraryInput.cancel" />
                </Button>
                <Button bsStyle="primary" disabled={!isReady || isLoading} onClick={handleDispatch}>
                    <span className="uikit-icon icon-send margin-right-5" />
                    {isLoading ? (
                        <FormattedMessage id="intl-msg:arbitraryInput.sending" />
                    ) : (
                        <FormattedMessage id="intl-msg:arbitraryInput.dispatchMessage" />
                    )}
                </Button>
            </div>
        </div>
    );

    return (
        <Dialog
            show={isOpen}
            onClose={onClose}
            title={
                <span className="display-flex align-items-center gap-10">
                    <span className="uikit-icon icon-prompt text-color-primary" />
                    <span>
                        <FormattedMessage id="intl-msg:arbitraryInput.title" />
                        <div className="text-size-10 text-color-gray text-uppercase margin-top-3">
                            <FormattedMessage id="intl-msg:arbitraryInput.subtitle" />
                        </div>
                    </span>
                </span>
            }
            body={body}
            footer={footer}
            bsSize="fullheight-lg"
            bodyClassName="display-flex flex-column overflow-hidden"
            showCloseButton
        />
    );
};

export default ArbitraryInputModal;
